import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { getHourlyUsers, assignTemporaryAdminProject } from '../../../../services/userService';

const AssignAdminDialog = ({ open, handleClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open) {
      fetchHourlyUsers();
    } else {
      // Reset state when dialog closes
      setSelectedUsers([]);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const fetchHourlyUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const hourlyUsers = await getHourlyUsers();
      console.log('Fetched hourly users:', hourlyUsers);
      
      if (!hourlyUsers || hourlyUsers.length === 0) {
        console.warn('No hourly users returned from API');
      }
      
      setUsers(hourlyUsers || []);
    } catch (err) {
      console.error('Error fetching hourly users:', err);
      setError(err.message || 'Failed to fetch hourly users');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(users.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleAssign = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (selectedUsers.length === 0) {
        setError('Please select at least one user');
        return;
      }
      
      setLoading(true);
      const result = await assignTemporaryAdminProject(selectedUsers);
      
      console.log('Assignment result:', result);
      
      // Reset selection
      setSelectedUsers([]);
      
      // Refresh users list if needed
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the dialog after successful assignment
      handleClose();
    } catch (err) {
      console.error('Error assigning Administrative project:', err);
      
      // Handle specific error messages
      if (err.message?.includes('team')) {
        setError('You can only assign the Administrative project to members of your teams');
      } else if (err.message?.includes('hourly')) {
        setError('Some selected users are not active hourly employees');
      } else if (err.message?.includes('Administrative project not found')) {
        setError('The Administrative project does not exist in the system');
      } else {
        setError(err.message || 'Failed to assign Administrative project');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" // Changed back to "md" for better description layout
      fullWidth
      PaperProps={{
        sx: {
          width: '45%', // Further reduced width by 15% (from 60% to 45%)
          maxWidth: '500px' // Further reduced max width
        }
      }}
    >
      <DialogTitle>Assign Administrative Project</DialogTitle>
      <Box sx={{ px: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This will temporarily assign the Administrative project to the selected hourly users for 24 hours.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <DialogContent sx={{ pt: 0, px: 2 }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: '427px', // Reduced height by 5% (from 450px to 427px)
              overflow: 'auto'
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      width: '60px', // Reduced width for enable column
                      py: 1 // Reduced padding
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                        checked={users.length > 0 && selectedUsers.length === users.length}
                        onChange={handleSelectAll}
                        size="small" // Smaller checkbox
                      />
                      <span>Enable</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          size="small" // Smaller checkbox
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{`${user.firstName} ${user.lastName} (${user.username})`}</TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No active hourly users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleAssign} 
          variant="contained" 
          color="primary"
          disabled={selectedUsers.length === 0}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignAdminDialog;
